import { isEmpty, isNumber } from 'lodash';
import { IntlShape } from 'react-intl';
import { ItemStatus } from '@prisma/client';
import { RootStateOrAny } from 'react-redux';
import {
  BundleStatus,
  CoreListingLineItem,
  CoreShippingLineItem,
  FulfillmentMethod,
  LineItemCode,
  Participant,
} from '../../types/apollo/generated/types.generated';
import { BundleInfo } from '../../types/models/bundle';
import { ListingItemType } from '../../types/sharetribe/listing';
import {
  convertMarketplacePriceToShopifyCurrency,
  formatDollarAmountAsCurrency,
} from '../../util/currency';
import { getTotalPayoutValueForListings } from '../../util/listings/listing';
import { getBundleListings } from '../../util/bundles';
import { BrandCountryConfigType } from '../../hooks/useCountryConfig';
import { PayoutOptions } from '../../util/constants';

// Filter values for brands
export const BUNDLE_STATUS_FILTER_ALL = 'ALL';
export type StatusFilterValues = BundleStatus | typeof BUNDLE_STATUS_FILTER_ALL;
export enum FilterQueryParam {
  Filter = 'filter',
  Query = 'query',
}

export const isBundleCancellable = (participant: Participant, bundle: BundleInfo) => {
  const isSeller = participant === Participant.Seller;
  return isSeller && bundle.status === BundleStatus.Open;
};

export interface GetPayoutValuesOptions {
  needsVerifiedBundleItems?: boolean;
  includeShippingReimbursement?: boolean;
}

type ShippingReimbursementValues = {
  value: number;
  formattedValue: string;
  payoutValuesWithShipping: Partial<Record<PayoutOptions, string>> & {
    creditAmount?: number;
    creditInBrandShopifyCurrency?: string;
  };
};

export interface PayoutValues {
  cash?: string;
  credit?: string;
  creditAmount?: number;
  creditInBrandShopifyCurrency?: string;
  shippingReimbursementInfo?: ShippingReimbursementValues;
}

export const shouldIncludeShippingReimbursement = (
  bundle: BundleInfo | undefined,
  isSellerGeneratedLabelsEnabled: boolean
) =>
  // Shipping reimbursement should be included with the payout values if it was shipped with a
  // seller-generated label, or if the shop has seller-generated labels enabled.
  bundle?.fulfillment
    ? bundle.fulfillment.method === FulfillmentMethod.Peer
    : isSellerGeneratedLabelsEnabled;

export const getShippingReimbursementValues = (
  shippingLineItem: CoreShippingLineItem,
  basePayoutAmounts: Partial<Record<PayoutOptions, number>>,
  intl: IntlShape,
  brandCountryConfig: BrandCountryConfigType
): ShippingReimbursementValues => {
  const { currencyConfig, brandHasShopifyAndTreetCurrencyMismatch } = brandCountryConfig;
  const marketplaceCurrency = currencyConfig.currency;
  const shippingValue = shippingLineItem.price;
  const formattedShippingValue = formatDollarAmountAsCurrency(
    intl,
    shippingValue,
    marketplaceCurrency
  );

  const basePayoutValuesWithShipping = Object.entries(basePayoutAmounts).reduce(
    (acc, [key, value]) => {
      if (isNumber(value)) {
        const total = value + shippingValue;
        acc.amounts[key as PayoutOptions] = total;
        acc.formatted[key as PayoutOptions] = formatDollarAmountAsCurrency(
          intl,
          total,
          marketplaceCurrency
        );
      }
      return acc;
    },
    { amounts: {}, formatted: {} } as {
      amounts: Partial<Record<PayoutOptions, number>>;
      formatted: Partial<Record<PayoutOptions, string>>;
    }
  );

  const payoutValuesWithShipping: ShippingReimbursementValues['payoutValuesWithShipping'] = {
    ...basePayoutValuesWithShipping.formatted,
  };

  if (isNumber(basePayoutValuesWithShipping.amounts.credit)) {
    payoutValuesWithShipping.creditAmount = basePayoutValuesWithShipping.amounts.credit;

    if (brandHasShopifyAndTreetCurrencyMismatch) {
      const creditInBrandShopifyCurrencyNumber = convertMarketplacePriceToShopifyCurrency(
        basePayoutValuesWithShipping.amounts.credit,
        brandCountryConfig
      );

      payoutValuesWithShipping.creditInBrandShopifyCurrency = formatDollarAmountAsCurrency(
        intl,
        creditInBrandShopifyCurrencyNumber.amount,
        creditInBrandShopifyCurrencyNumber.currency
      );
    }
  }

  return {
    value: shippingValue,
    formattedValue: formattedShippingValue,
    payoutValuesWithShipping,
  };
};

// Get the potential payout amount for cash/credit options.
// Convert the payout credit price if the brand pays out credit in
// currency that differs from the marketplace.
export const getPayoutValues = (
  bundle: BundleInfo | undefined,
  intl: IntlShape,
  brandCountryConfig: BrandCountryConfigType,
  rootState: RootStateOrAny,
  options?: GetPayoutValuesOptions
): PayoutValues => {
  const lineItems = bundle?.lineItems ?? [];
  let bundleItems = bundle?.bundleItems ?? [];
  const { needsVerifiedBundleItems = false, includeShippingReimbursement = false } = options ?? {};
  if (needsVerifiedBundleItems) {
    bundleItems = bundle?.bundleItems.filter((item) => item.status === ItemStatus.VERIFIED) || [];
  }
  const { brandHasShopifyAndTreetCurrencyMismatch, currencyConfig } = brandCountryConfig;
  const sharetribeListingIds = bundleItems?.map(
    (bundleItem) => bundleItem.listing.sharetribeListingId
  );

  // Only include line items relevant to the chosen transactions.
  const listingLineItems = lineItems
    ?.map((lineItem) => {
      if (
        lineItem?.listingLineItem?.sharetribeListingId &&
        sharetribeListingIds.includes(lineItem.listingLineItem?.sharetribeListingId)
      ) {
        return lineItem.listingLineItem;
      }
      return null;
    })
    .filter((lineItem) => !!lineItem) as CoreListingLineItem[];

  const listingsFromBundle = getBundleListings(bundle, rootState);
  const { cash, credit } = getTotalPayoutValueForListings(listingsFromBundle, listingLineItems);

  const isTradeIn =
    !isEmpty(listingsFromBundle) &&
    listingsFromBundle.every(
      (listing) => listing.attributes.publicData.listingItemType === ListingItemType.TradeIn
    );

  let creditInBrandShopifyCurrency;
  if (brandHasShopifyAndTreetCurrencyMismatch && credit) {
    creditInBrandShopifyCurrency = convertMarketplacePriceToShopifyCurrency(
      credit,
      brandCountryConfig
    );
  }

  const marketplaceCurrency = currencyConfig.currency;

  // Reimburse the seller for shipping costs
  const shippingLineItem = bundle?.lineItems.find(
    ({ code, includeFor }) =>
      code === LineItemCode.Shipping && includeFor.includes(Participant.Seller)
  )?.shippingLineItem;
  const shippingReimbursement =
    includeShippingReimbursement &&
    !!shippingLineItem &&
    getShippingReimbursementValues(shippingLineItem, { cash, credit }, intl, brandCountryConfig);

  return {
    ...(cash && { cash: formatDollarAmountAsCurrency(intl, cash, marketplaceCurrency) }),
    // Trade Ins can have credit === 0
    ...((credit || isTradeIn) && {
      credit: formatDollarAmountAsCurrency(intl, credit, marketplaceCurrency),
    }),
    creditAmount: credit,
    ...(creditInBrandShopifyCurrency && {
      creditInBrandShopifyCurrency: formatDollarAmountAsCurrency(
        intl,
        creditInBrandShopifyCurrency.amount,
        creditInBrandShopifyCurrency.currency
      ),
    }),
    ...(shippingReimbursement && { shippingReimbursementInfo: shippingReimbursement }),
  };
};
